import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getStudentMe, resolveStudentGrade, type RosterStudent } from '../api/roster.client';
import { ROSTER_STUDENT_ME_QUERY_KEY } from '../queryKeys';
import { shouldFetchStudentRosterMe } from '../studentRosterEligibility';

const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Cached roster student profile for the current session.
 * Uses React Query: refetches after staleTime; cleared on logout (see AuthContext).
 *
 * **Role:** only runs `GET /v1/roster/students/me` when the authenticated user has
 * `STUDENT` in `user.roles`. Teacher/admin/parent layouts must not hit this endpoint.
 */
export function useStudentMe() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const enabled = shouldFetchStudentRosterMe(authLoading, isAuthenticated, user);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (authLoading || !isAuthenticated || !user) return;
    if (shouldFetchStudentRosterMe(false, true, user)) return;
    console.debug(
      '[roster] useStudentMe: skipping GET /v1/roster/students/me — user is not a student',
      { roles: user.roles }
    );
  }, [authLoading, isAuthenticated, user]);

  const query = useQuery<RosterStudent, Error>({
    queryKey: ROSTER_STUDENT_ME_QUERY_KEY,
    queryFn: getStudentMe,
    staleTime: STALE_TIME_MS,
    enabled,
  });

  const student = query.data;
  const grade = useMemo(() => (student ? resolveStudentGrade(student) : null), [student]);

  /** True while resolving auth or the first active fetch of students/me. */
  const isRosterLoading = authLoading || (enabled && query.isLoading);

  return {
    ...query,
    authLoading,
    enabled,
    student,
    grade,
    isRosterLoading,
  };
}
